import type { AxiosError } from 'axios'
import axios from 'axios'
import { Constants } from '../utils/enums'
import { isWebSessionRole } from '@/types/user.type'
import { createDiscreteApi } from 'naive-ui'
const { message } = createDiscreteApi(['message'])

// Define Axios Response / Error Types
export type _AxiosError = AxiosError & {
  response: {
    data: { message: string }
  }
}

const loginUrl = '/auth/anonymous/login'
const anonymousRefreshUrl = '/auth/anonymous/refresh'
const webRefreshUrl = '/auth/web/refresh'

const AUTH_SKIP_AUTO_REFRESH_URLS = new Set([
  loginUrl,
  anonymousRefreshUrl,
  webRefreshUrl,
  '/auth/anonymous/signup',
  '/auth/anonymous/signout',
  '/auth/hello'
])

const axiosHotelLovers = axios.create({
  baseURL: Constants['BASE_URL_API'],
  withCredentials: true,
  timeout: 300_000 // 5分
})

let refreshPromise: Promise<void> | null = null

async function refreshSession(): Promise<void> {
  const { useAuthStore } = await import('@/stores/authStore')
  const authStore = useAuthStore()
  const role = authStore.current_user?.role
  const useWebFirst = role !== undefined && isWebSessionRole(role)

  const tryWeb = () => authStore.webRefresh()
  const tryAnonymous = async () => {
    const gpId = await authStore.load_gp_cookie().catch(() => null)
    await authStore.refresh_anonymous(gpId)
  }

  try {
    await (useWebFirst ? tryWeb : tryAnonymous)()
  } catch {
    await (useWebFirst ? tryAnonymous : tryWeb)()
  }
}

axiosHotelLovers.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const requestUrl = err.config?.url ?? ''
    if (
      err.config &&
      err.response?.status === 401 &&
      !AUTH_SKIP_AUTO_REFRESH_URLS.has(requestUrl)
    ) {
      if (!refreshPromise) {
        refreshPromise = refreshSession().finally(() => {
          refreshPromise = null
        })
      }
      try {
        await refreshPromise
        return axiosHotelLovers.request(err.config)
      } catch {
        // refresh 失敗は chatbotView の ensureSession に委譲
      }
    } else if (err.code === 'ECONNABORTED') {
      message.error('タイムアウトしました')
    } else if (!err?.response) {
      message.error('ネットワークエラーが発生しました')
    }
    return Promise.reject(err)
  }
)

export default axiosHotelLovers
